<template>
    <rested-card v-if="(isSpaceArchived || isSpaceRested) && isDevelopment"></rested-card>
    <v-card flat color="transparent" v-else class="w-100 align-self-start">
        <v-card-title>Exported applications</v-card-title>
        <v-card-text>
            <div class="d-flex align-center">
                <v-spacer></v-spacer>
                <v-text-field
                    prepend-inner-icon="mdi-magnify "
                    autofocus
                    hide-details
                    solo
                    flat
                    dense
                    placeholder="Search for an exported application..."
                    v-model="search"
                    clearable
                    @click:clear="removeSearchQuery"
                    class="mr-2">
                    <template slot="append">
                        <v-menu offset-y left>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn icon v-bind="attrs" v-on="on">
                                    <v-icon>mdi-cog</v-icon>
                                </v-btn>
                            </template>
                            <v-list>
                                <v-list-item>
                                    <template v-slot:default>
                                        <v-list-item-action>
                                            <v-checkbox v-model="includeFailed">
                                                <template v-slot:label>
                                                    <span class="secondary--text mx-3">Include failed</span>
                                                </template>
                                            </v-checkbox>
                                        </v-list-item-action>
                                    </template>
                                </v-list-item>
                                <v-list-item>
                                    <template v-slot:default>
                                        <v-list-item-action>
                                            <v-checkbox v-model="includeDeleted">
                                                <template v-slot:label>
                                                    <span class="secondary--text mx-3">Include deleted</span>
                                                </template>
                                            </v-checkbox>
                                        </v-list-item-action>
                                    </template>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>
                </v-text-field>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn
                            @click="$store.dispatch('snapshotStore/fetchExportedImages', { snid: $route.params.snid, query: imageListQuery })"
                            icon
                            v-on="on"
                            :loading="exportedImagesFetching">
                            <v-icon>refresh</v-icon>
                        </v-btn>
                    </template>
                    <span>Refresh exported applications</span>
                </v-tooltip>
            </div>
            <v-data-table
                :search="search"
                item-key="deid"
                :headers="headers"
                v-model="selected"
                :items="exportedImages"
                :options="tableOptions"
                :loading="exportedImagesFetching"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                class="mt-4">
                <template v-slot:top="{ options, pagination, updateOptions }">
                    <div class="d-flex align-center">
                        <v-spacer></v-spacer>
                        <v-data-footer
                            :pagination="pagination"
                            :options="options"
                            @update:options="updateOptions"
                            :itemsPerPageOptions="itemsPerPageOptions"
                            items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                            class="no-border" />
                    </div>
                </template>
                <template v-slot:no-data>Currently this space has no exported environments.</template>
                <template v-slot:[`item.exit_code`]="{ item }">
                    <v-chip small color="warning" v-if="item.delete_timestamp">Deleted</v-chip>
                    <v-chip small color="primary" v-else-if="!item.end_timestamp">In progress</v-chip>
                    <v-chip small color="success" v-else-if="item.exit_code === 0">Successful</v-chip>
                    <v-chip small color="error" v-else>Failed</v-chip>
                </template>
                <template v-slot:[`item.app_name`]="{ item }">
                    <span>{{ item.app_name }}</span>
                </template>
                <template v-slot:[`item.app_description`]="{ item }">
                    <span>{{ item.app_description }}</span>
                </template>

                <template v-slot:[`item.end_timestamp`]="{ item }">
                    <span v-if="item.end_timestamp">{{ item.end_timestamp | dateTimeToHuman }}</span>
                    <span v-else>-</span>
                </template>
                <template v-slot:[`item.docker_url`]="{ item }">
                    <span v-if="item.exit_code === 0">
                        <span>{{ item.docker_url }}</span>
                    </span>
                    <span v-else>-</span>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-menu left offset-x nudge-top="10" v-if="!item.delete_timestamp && item.exit_code === 0">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" text color="secondary" small>
                                <v-icon>more_horiz</v-icon>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <copy-to-clipboard :textToCopy="item.run_command" buttonName="Copy run command" type="listItem"></copy-to-clipboard>
                            <v-divider class="my-2"></v-divider>
                            <v-list-item @click="openDeleteDialog(item)">
                                <v-list-item-icon>
                                    <v-progress-circular
                                        v-if="deletingImage?.deid === item.deid && deleteRequestRunning"
                                        size="18"
                                        width="2"
                                        indeterminate
                                        color="error" />
                                    <v-icon v-else color="error">mdi-trash-can-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title class="error--text">Delete exported application</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </template>
            </v-data-table>
        </v-card-text>
    </v-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import eventBus from '@/components/EventBus'

const CopyToClipboard = () => import('@/components/CopyToClipboard.vue')
const RestedCard = () => import('@/modules/snapshot/components/RestedCard.vue')

export default {
    components: { CopyToClipboard, RestedCard },
    data() {
        return {
            search: '',
            headers: [
                { text: 'Status', align: 'left', value: 'exit_code' },
                { text: 'Name', align: 'left', value: 'app_name' },
                { text: 'Description', align: 'left', value: 'app_description' },
                { text: 'Export date', align: 'left', value: 'end_timestamp' },
                { text: 'Image tag', align: 'left', value: 'docker_url' },
                { text: 'Actions', align: 'right', value: 'actions', sortable: false }
            ],
            error: false,
            errorContent: '',
            selected: [],
            deleteDialog: false,
            deletingImage: null,
            deleteRequestRunning: false,
            includeFailed: true,
            includeDeleted: true,
            tableOptions: {
                page: 0,
                itemsPerPage: 25,
                pageStart: 1,
                pageStop: 25,
                itemsLength: 25
            },
            itemsPerPageOptions: [25, 50, 100, -1]
        }
    },
    created() {
        this.$store.dispatch('snapshotStore/fetchExportedImages', { snid: this.$route.params.snid, query: this.imageListQuery })
        if (this.$route.query.search) this.search = this.$route.query.search
        eventBus.$on('global-dialog-accept', evt => {
            this.sendDeleteRequest()
        })
    },
    destroyed() {
        eventBus.$off('global-dialog-accept')
    },
    computed: {
        ...mapState('snapshotStore', ['exportedImages', 'exportedImagesFetching']),
        ...mapGetters('spaceStore', ['isSpaceArchived', 'isSpaceRested']),
        ...mapGetters('snapshotStore', ['isDevelopment']),
        imageListQuery() {
            return `?include_deleted=${this.includeDeleted}&include_failed=${this.includeFailed}`
        },
        imageTag() {
            if (!this.deletingImage) return ''
            return this.deletingImage.docker_url.split(':')[1]
        }
    },
    methods: {
        removeSearchQuery() {
            this.$router.replace({
                ...this.$router.currentRoute,
                query: {
                    search: undefined
                }
            })
        },
        openDeleteDialog(image) {
            this.deletingImage = image
            this.$store.dispatch('showGlobalDialog', {
                dialogText: `Are you sure you would like to delete the exported application <em>${this.imageTag}</em>?`,
                dialogAction: ['delete', 'cancel'],
                dialogTitle: 'Confirm deletion'
            })
        },
        closeDeleteDialog() {
            this.deletingImage = null
        },
        async sendDeleteRequest() {
            this.deleteRequestRunning = true
            try {
                await this.$axios.delete(`/applications/exports/${this.deletingImage.deid}`)
                this.$store.dispatch('showSnackBar', {
                    snackBarText: `Successfully deleted ${this.deletingImage.app_name}.`,
                    snackBarTimeout: 5000,
                    snackBarIcon: 'check_circle'
                })
                this.$store.dispatch('snapshotStore/fetchExportedImages', { snid: this.$route.params.snid, query: this.imageListQuery })
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: `There was an error while deleting the environment. Please try again later.`,
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            }
            this.deletingImage = null
        }
    },
    watch: {
        imageListQuery(to) {
            this.$store.dispatch('snapshotStore/fetchExportedImages', { snid: this.$route.params.snid, query: to })
        }
    }
}
</script>
